import React from 'react';
import { Handle, Position } from 'reactflow';
import styled, { css, keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { VACANT_WARN_DAYS } from '../orgChartModel';

// Organizasyon şeması kartı (spec 2026-10-24 §5.3) — 232×72, sade: avatar, ad, ünvan, departman şeridi, en çok iki durum
// rozeti ve alt kenarda sayı rozeti. Kartta aksiyon YOK: yönetici araçları ve Düzenle aksiyonları ayrıntı panelinde
// (OrgDetailsPanel). Renk YALNIZ departmanı anlatır (`data.color`); durumlar rozettir.

const MAX_BADGES = 2;
// Departmansız / renksiz kart (tema dışı bağlamda — dışa aktarma, test — da aynı değer).
const NEUTRAL = '#A0A8B4';

export function countLabel(data) {
  const direct = data.directCount || 0;
  return typeof data.totalHeadcount === 'number' && data.totalHeadcount > direct ? `${direct} · ${data.totalHeadcount}` : `${direct}`;
}

export function countTooltip(t, data) {
  const direct = data.directCount || 0;
  if (typeof data.totalHeadcount !== 'number') return t('orgChart.countTooltipDirect', { direct });
  if (typeof data.totalPositionCount === 'number') {
    return t('orgChart.countTooltipPositions', { direct, total: data.totalHeadcount, positions: data.totalPositionCount });
  }
  return t('orgChart.countTooltip', { direct, total: data.totalHeadcount });
}

// Rozetler öncelik sırasıyla; ilk MAX_BADGES karta, kalanı yalnız panelde.
export function statusBadges(data) {
  const out = [];
  if (data.isOwn) out.push({ key: 'own', label: ['orgChart.you'], tone: 'own' });
  const scoped = Boolean(data.inManagedScope);
  if (scoped && data.isAssigned && data.occupantLeaving) out.push({ key: 'leaving', label: ['orgChart.scope.leaving'], tone: 'warn' });
  if (scoped && !data.isAssigned && typeof data.vacantSinceDays === 'number') {
    out.push({
      key: 'vacant-days',
      label: ['orgChart.scope.vacantForDays', { count: data.vacantSinceDays }],
      tone: data.vacantSinceDays > VACANT_WARN_DAYS ? 'danger' : 'muted',
    });
  }
  if (scoped && data.openRequisitionId) out.push({ key: 'requisition', label: ['orgChart.requisitionOpen'], tone: 'info' });
  if (data.variant === 'edit' && data.isAssigned) {
    const type = (data.assignmentType || '').toLowerCase();
    if (type === 'secondary') out.push({ key: 'assignment', label: ['orgChart.assignmentSecondary'], tone: 'muted' });
    if (type === 'proxy') out.push({ key: 'assignment', label: ['orgChart.assignmentProxy'], tone: 'warn' });
  }
  return out;
}

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(2, 145, 239, 0.55); }
  100% { box-shadow: 0 0 0 16px rgba(2, 145, 239, 0); }
`;

const Card = styled.div`
  width: 232px;
  height: 72px;
  box-sizing: border-box;
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px 0 14px;
  border-radius: 14px;
  border: 1px ${({ $vacant }) => ($vacant ? 'dashed' : 'solid')} ${({ $selected, theme }) => ($selected ? theme.colors.primary.main : '#E6EAF0')};
  background: ${({ $vacant, $own }) => {
    if ($vacant) return '#FAFBFC';
    return $own ? 'linear-gradient(180deg, #F2F9FF, #fff)' : '#fff';
  }};
  box-shadow: ${({ $selected }) => ($selected
    ? '0 0 0 3px rgba(2, 145, 239, 0.18), 0 6px 18px rgba(15, 23, 42, 0.10)'
    : '0 1px 2px rgba(15, 23, 42, 0.05)')};
  font-family: 'Nunito Sans', sans-serif;
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s;
  /* React Flow v11, düğüm ne sürüklenebilir ne seçilebilirse sarmalayıcı .react-flow__node'a INLINE
     pointer-events: none yazar; özellik kalıtsaldır ve kartı öldürür (2026-09-25 regresyonu). Kart kendi vuruş testini
     geri açar — sürükleme/seçim kapalı kalır, tuval kaydırması yukarı baloncuklanır. */
  pointer-events: auto;
  &:hover { box-shadow: 0 6px 18px rgba(15, 23, 42, 0.10); }
  ${({ $highlight }) => $highlight && css`animation: ${pulse} 1.2s ease-out 2;`}
`;

const DeptStripe = styled.span`
  position: absolute;
  left: 0;
  top: 14px;
  bottom: 14px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: ${({ $color }) => $color};
`;

const AvatarCircle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-size: 14px;
  font-weight: 800;
  color: #fff;
  background: ${({ $color }) => $color};
  ${({ $vacant, theme }) => $vacant && css`
    background: #EEF1F5;
    color: ${theme.colors.orgChart.neutral};
    border: 1.5px dashed #C3CBD5;
  `}
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const Who = styled.div`
  flex: 1;
  min-width: 0;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const Name = styled.div`
  font-size: 14px;
  font-weight: ${({ $vacant }) => ($vacant ? 600 : 700)};
  color: ${({ $vacant, theme }) => ($vacant ? theme.colors.text.muted : '#1F2A37')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 1px;
`;

const TONES = {
  own: { bg: '#0291EF', color: '#fff' },
  warn: { bg: '#FEF3C7', color: '#B45309' },
  danger: { bg: '#FDECEA', color: '#E53935' },
  info: { bg: '#EFF6FF', color: '#2563EB' },
  muted: { bg: '#F0F2F5', color: '#6B7280' },
};

const Badge = styled.span`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 800;
  padding: 1px 6px;
  border-radius: 5px;
  white-space: nowrap;
  background: ${({ $tone }) => TONES[$tone].bg};
  color: ${({ $tone }) => TONES[$tone].color};
`;

const Pill = styled.button`
  position: absolute;
  left: 50%;
  bottom: -11px;
  transform: translateX(-50%);
  height: 22px;
  min-width: 34px;
  padding: 0 8px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  white-space: nowrap;
  font: 700 11px 'Nunito Sans', sans-serif;
  cursor: pointer;
  z-index: 2;
  border: 1px solid ${({ $open }) => ($open ? '#1F2A37' : '#E6EAF0')};
  background: ${({ $open }) => ($open ? '#1F2A37' : '#fff')};
  color: ${({ $open, theme }) => ($open ? '#fff' : theme.colors.text.muted)};
  &:hover { border-color: ${({ theme }) => theme.colors.primary.main}; }
`;

const hiddenHandle = { opacity: 0, width: 1, height: 1, minWidth: 0, minHeight: 0, border: 0, pointerEvents: 'none' };
const editHandle = { width: 10, height: 10, background: '#D0D5DD', border: '2px solid #fff', borderRadius: '50%' };
// Kaynak tutamaç (sürükleyerek üst değiştirme) alt kenarın ORTASINDAKİ sayı rozetinin altında kalırdı: sağa alınır ve
// rozetin üstünde durur — basış rozeti değil tutamacı yakalar.
const editSourceHandle = { ...editHandle, left: '85%', zIndex: 3 };

function initials(name) {
  return (name || '').split(' ').filter(Boolean).slice(0, 2).map((s) => s[0]).join('').toLocaleUpperCase('tr') || '?';
}

export default function OrgCard({ data }) {
  const { t } = useTranslation();
  const isVacant = !data.isAssigned;
  const direct = data.directCount || 0;
  const connectable = Boolean(data.variant === 'edit' && data.connectable);
  const color = data.color || NEUTRAL;
  const badges = statusBadges(data).slice(0, MAX_BADGES);
  const name = isVacant ? t('orgChart.vacantPosition') : data.userName;

  return (
    <Card
      data-testid={`org-card-${data.positionId}`}
      data-variant={data.variant || 'view'}
      data-selected={data.isSelected ? 'true' : undefined}
      data-highlight={data.highlight ? 'true' : undefined}
      data-color={color}
      data-vacant={isVacant ? 'true' : undefined}
      title={[data.titleName, data.departmentName].filter(Boolean).join(' · ') || undefined}
      $selected={Boolean(data.isSelected)}
      $highlight={Boolean(data.highlight)}
      $vacant={isVacant}
      $own={Boolean(data.isOwn)}
      onClick={() => data.onSelect?.(data.positionId)}
    >
      <Handle type="target" position={Position.Top} style={connectable ? editHandle : hiddenHandle} isConnectable={connectable} />
      <DeptStripe data-testid="dept-stripe" data-color={color} $color={color} />
      <AvatarCircle $color={color} $vacant={isVacant}>
        {!isVacant && data.profilePicture
          ? <img src={data.profilePicture} alt="" data-testid="card-photo" />
          : <span data-testid="card-initials">{isVacant ? '?' : initials(data.userName)}</span>}
      </AvatarCircle>
      <Who>
        <NameRow>
          <Name $vacant={isVacant}>{name}</Name>
          {badges.map((b) => (
            <Badge key={b.key} data-testid={`badge-${b.key}`} data-tone={b.tone} $tone={b.tone}>{t(...b.label)}</Badge>
          ))}
        </NameRow>
        <Title>{data.titleName || data.departmentName || '-'}</Title>
      </Who>
      {direct > 0 && (
        // Tooltip bileşeni position:relative sarmalayıcı açar ve mutlak konumlu rozeti kaydırır — yerine yerel title.
        <Pill
          type="button"
          title={countTooltip(t, data)}
          data-testid="toggle-children"
          aria-expanded={Boolean(data.isExpanded)}
          aria-label={data.isExpanded ? t('orgChart.collapse') : t('orgChart.expand')}
          $open={Boolean(data.isExpanded)}
          onClick={(e) => {
            e.stopPropagation();
            data.onToggleChildren?.(data.positionId);
          }}
        >
          {data.loadingChildren && <CircularProgress size={10} />}
          {!data.loadingChildren && (data.isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
          <span data-testid="count-badge">{countLabel(data)}</span>
        </Pill>
      )}
      <Handle type="source" position={Position.Bottom} style={connectable ? editSourceHandle : hiddenHandle} isConnectable={connectable} />
    </Card>
  );
}
